// src/components/leave/LeaveTypePicker.jsx
//
// Leave Type Picker
// =================
//
// PURPOSE
// -------
// hb_visits started with only a handful of leave types, where a native
// <select> was good enough.  The Leave / HR module is now much richer:
// statutory Polish leave types, HR-confirmed medical records, parental
// contexts, cancellation/storno, and application-specific work-time debt
// events all live in the same request dialog.
//
// A long flat dropdown makes users slow down, scan too much text, and increases
// the risk of choosing the wrong type.  This component replaces the raw select
// with a task-oriented picker:
//
// - grouped by business meaning,
// - searchable by name, technical code, and human aliases,
// - numeric quick selection for keyboard-driven users,
// - subtle category colors for correction/storno and time-debt entries,
// - recent and favorite shortcuts persisted in localStorage,
// - compact selected summary so the modal does not stay visually overloaded.
//
// IMPORTANT ARCHITECTURAL NOTE
// ----------------------------
// This is intentionally frontend-only.  It does not change leave type IDs,
// backend contracts, balance accounting, approval, entitlement contexts, or the
// future calendar/read-model work.  The parent modal still stores a plain
// `leaveTypeId` and submits the same payload as before.
//
// ACCESSIBILITY / KEYBOARD MODEL
// ------------------------------
// The picker follows a lightweight combobox/listbox interaction model suitable
// for an in-modal picker:
//
// - typing filters the list,
// - a numeric value like "03" jumps to the visible quick number,
// - ArrowDown / ArrowUp moves the active option,
// - Enter selects the active option or exact quick number,
// - Escape collapses the picker,
// - clicking a star toggles a favorite without selecting the option.
//
// The visible quick numbers are deliberately UI shortcuts, not business codes.
// Stable business identification remains the backend LeaveType.code / id.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_VERSION = "v1";
const MAX_RECENT_ITEMS = 6;

function safeStr(value) {
  return value == null ? "" : String(value);
}

function normalize(value) {
  return safeStr(value).trim().toLowerCase();
}

function normalizeCode(code) {
  const c = safeStr(code).trim();
  return c ? c.toUpperCase() : "";
}

function normalizeAccountingType(value) {
  return normalizeCode(value || "NO_BALANCE_EFFECT");
}

function normalizeUnit(value) {
  return normalizeCode(value || "DAYS");
}

function getLeaveTypeLabel(leaveType) {
  if (!leaveType) return "—";
  return leaveType.name_pl || leaveType.name_en || leaveType.code || `#${leaveType.id}`;
}

function getStorageKey(scope) {
  const normalizedScope = normalize(scope || "default") || "default";
  return `hb_visits.leaveTypePicker.${STORAGE_VERSION}.${normalizedScope}`;
}

function readStoredPickerState(scope) {
  if (typeof window === "undefined" || !window.localStorage) {
    return { recentIds: [], favoriteIds: [] };
  }

  try {
    const raw = window.localStorage.getItem(getStorageKey(scope));
    if (!raw) return { recentIds: [], favoriteIds: [] };

    const parsed = JSON.parse(raw);
    return {
      recentIds: Array.isArray(parsed?.recentIds) ? parsed.recentIds.map(String) : [],
      favoriteIds: Array.isArray(parsed?.favoriteIds) ? parsed.favoriteIds.map(String) : [],
    };
  } catch (_err) {
    return { recentIds: [], favoriteIds: [] };
  }
}

function writeStoredPickerState(scope, state) {
  if (typeof window === "undefined" || !window.localStorage) return;

  try {
    window.localStorage.setItem(
      getStorageKey(scope),
      JSON.stringify({
        recentIds: Array.isArray(state?.recentIds) ? state.recentIds.map(String) : [],
        favoriteIds: Array.isArray(state?.favoriteIds) ? state.favoriteIds.map(String) : [],
      })
    );
  } catch (_err) {
    // localStorage can be disabled in private browsers or locked-down clients.
    // The picker remains fully functional without persistence.
  }
}

function uniqStrings(values) {
  const seen = new Set();
  const out = [];

  (values || []).forEach((value) => {
    const str = String(value || "").trim();
    if (!str || seen.has(str)) return;
    seen.add(str);
    out.push(str);
  });

  return out;
}

function codeAliases(code) {
  const c = normalizeCode(code);

  const aliases = {
    VACATION: ["wypoczynkowy", "holiday", "annual", "urlop"],
    ON_REQUEST: ["na żądanie", "zadanie", "request", "urgent vacation"],
    UNPAID: ["bezpłatny", "unpaid", "bezplatny"],
    SPECIAL_LEAVE: ["okolicznościowy", "okolicznosciowy", "special", "wedding", "funeral"],
    FORCE_MAJEURE: ["siła wyższa", "sila wyzsza", "force majeure", "pilne"],
    CHILDCARE_ART_188: ["188", "dziecko", "childcare", "2 dni", "16 godzin"],
    CARE_LEAVE: ["opiekuńczy", "opiekunczy", "care", "5 dni"],
    CARE_ALLOWANCE_ABSENCE: ["zasiłek opiekuńczy", "zasilek opiekunczy", "chore dziecko", "opieka zus"],
    EDUCATIONAL_LEAVE: ["szkoleniowy", "education", "training", "study"],
    BLOOD_DONATION: ["krew", "krwiodawstwo", "blood", "donor"],
    HOLIDAY_COMPENSATION: ["święto", "swieto", "sobota", "compensation", "day off"],
    HOLIDAY_COMPENSATION_DAY_OFF: ["święto", "swieto", "sobota", "compensation", "day off"],
    DAY_OFF_FOR_HOLIDAY_ON_SATURDAY: ["święto", "swieto", "sobota", "compensation", "day off"],
    PATERNITY: ["ojcowski", "father", "paternity"],
    FATHERS_LEAVE: ["ojcowski", "father", "paternity"],
    MATERNITY: ["macierzyński", "macierzynski", "maternity"],
    MATERNITY_LEAVE: ["macierzyński", "macierzynski", "maternity"],
    PARENTAL: ["rodzicielski", "parental"],
    PARENTAL_LEAVE: ["rodzicielski", "parental"],
    ADOPTION_MATERNITY_LEAVE: ["adopcja", "adoption", "przysposobienie", "macierzyński adopcyjny"],
    SUPPLEMENTARY_MATERNITY_LEAVE: ["uzupełniający", "uzupelniajacy", "wcześniak", "wczesniak", "hospitalizacja"],
    CHILDCARE_LEAVE_LONG: ["wychowawczy", "long childcare", "36 miesięcy", "36 miesiecy"],
    SICK_LEAVE: ["l4", "chorobowe", "choroba", "sick", "zus", "ezla", "e-zla"],
    L4: ["sick", "chorobowe", "choroba", "zus", "ezla", "e-zla"],
    PREGNANCY_MEDICAL_EXAM: ["ciąża", "ciaza", "badania", "pregnancy", "exam"],
    PRIVATE_EXIT_TO_MAKE_UP: ["wyjście", "wyjscie", "prywatne", "private", "dług", "dlug", "debt", "odpracować"],
    MAKE_UP_WORK: ["odpracowanie", "make up", "make-up", "credit", "kredyt", "kompensata"],
    JOB_SEARCH_LEAVE: ["poszukiwanie pracy", "job search", "wypowiedzenie"],
    CANCELLATION: ["storno", "cancel", "cancellation", "anulowanie", "cofnięcie", "cofniecie", "korekta"],
  };

  return aliases[c] || [];
}

function classifyLeaveType(leaveType) {
  const code = normalizeCode(leaveType?.code);
  const accountingType = normalizeAccountingType(leaveType?.balance_accounting_type);

  if (code === "CANCELLATION") {
    return {
      groupKey: "corrections",
      variant: "danger",
      badgeKey: "storno",
      sortWeight: 90,
    };
  }

  if (accountingType === "ADD_TIME_DEBT") {
    return {
      groupKey: "workTimeAdjustments",
      variant: "blue",
      badgeKey: "addTimeDebt",
      sortWeight: 30,
    };
  }

  if (accountingType === "REDUCE_TIME_DEBT") {
    return {
      groupKey: "workTimeAdjustments",
      variant: "indigo",
      badgeKey: "reduceTimeDebt",
      sortWeight: 31,
    };
  }

  if ([
    "SICK_LEAVE",
    "L4",
    "PREGNANCY_MEDICAL_EXAM",
    "CARE_ALLOWANCE_ABSENCE",
    "CHILD_SICKNESS_CARE",
  ].includes(code)) {
    return {
      groupKey: "medicalHr",
      variant: "teal",
      badgeKey: "medicalHr",
      sortWeight: 20,
    };
  }

  if ([
    "MATERNITY",
    "MATERNITY_LEAVE",
    "PARENTAL",
    "PARENTAL_LEAVE",
    "PATERNITY",
    "PATERNITY_LEAVE",
    "FATHERS_LEAVE",
    "ADOPTION_MATERNITY_LEAVE",
    "SUPPLEMENTARY_MATERNITY_LEAVE",
    "CHILDCARE_ART_188",
    "CHILDCARE_188",
    "CHILDCARE_LEAVE_LONG",
    "UNPAID_CHILDCARE_LEAVE",
  ].includes(code)) {
    return {
      groupKey: "familyParenthood",
      variant: "violet",
      badgeKey: "family",
      sortWeight: 10,
    };
  }

  if (["VACATION", "ON_REQUEST"].includes(code) || accountingType === "DEDUCT_FROM_LEAVE_BALANCE") {
    return {
      groupKey: "leaveBalance",
      variant: "green",
      badgeKey: "balance",
      sortWeight: 0,
    };
  }

  if (["FORCE_MAJEURE", "HOLIDAY_COMPENSATION", "HOLIDAY_COMPENSATION_DAY_OFF", "DAY_OFF_FOR_HOLIDAY_ON_SATURDAY"].includes(code)) {
    return {
      groupKey: "workTimeAdjustments",
      variant: "slate",
      badgeKey: "workTime",
      sortWeight: 32,
    };
  }

  return {
    groupKey: "otherSpecial",
    variant: "neutral",
    badgeKey: "other",
    sortWeight: 50,
  };
}

function quickNumber(index) {
  return String(index + 1).padStart(2, "0");
}

function buildSearchText(item) {
  return normalize([
    item.quickNo,
    item.label,
    item.code,
    item.groupLabel,
    item.badgeLabel,
    item.unit,
    item.accountingType,
    ...(item.aliases || []),
  ].join(" "));
}

function optionMatches(item, query) {
  const q = normalize(query);
  if (!q) return true;

  // Numeric quick selection should work even though the number is only a UI
  // accelerator.  Both "3" and "03" match quick number "03".
  if (/^\d+$/.test(q)) {
    const normalizedQuick = String(Number(item.quickNo));
    return item.quickNo === q.padStart(2, "0") || normalizedQuick === q;
  }

  return item.searchText.includes(q);
}

function groupBy(items, keyGetter) {
  const map = new Map();
  items.forEach((item) => {
    const key = keyGetter(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return map;
}

export default function LeaveTypePicker({
  leaveTypes,
  value,
  onChange,
  disabled = false,
  storageScope = "default",
}) {
  const { t } = useTranslation(["common", "leaves"]);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [stored, setStored] = useState(() => readStoredPickerState(storageScope));

  useEffect(() => {
    setStored(readStoredPickerState(storageScope));
  }, [storageScope]);

  const groupLabels = useMemo(() => ({
    leaveBalance: t("leaves:modal.leaveTypePicker.groups.leaveBalance", { defaultValue: "Leave balance" }),
    familyParenthood: t("leaves:modal.leaveTypePicker.groups.familyParenthood", { defaultValue: "Family & parenthood" }),
    medicalHr: t("leaves:modal.leaveTypePicker.groups.medicalHr", { defaultValue: "Medical / HR confirmed" }),
    workTimeAdjustments: t("leaves:modal.leaveTypePicker.groups.workTimeAdjustments", { defaultValue: "Work-time adjustments" }),
    otherSpecial: t("leaves:modal.leaveTypePicker.groups.otherSpecial", { defaultValue: "Other / special" }),
    corrections: t("leaves:modal.leaveTypePicker.groups.corrections", { defaultValue: "Corrections / cancellation" }),
  }), [t]);

  const groupOrder = useMemo(
    () => [
      "leaveBalance",
      "familyParenthood",
      "medicalHr",
      "workTimeAdjustments",
      "otherSpecial",
      "corrections",
    ],
    []
  );

  const badgeLabels = useMemo(() => ({
    balance: t("leaves:modal.leaveTypePicker.badges.balance", { defaultValue: "Balance" }),
    family: t("leaves:modal.leaveTypePicker.badges.family", { defaultValue: "Family" }),
    medicalHr: t("leaves:modal.leaveTypePicker.badges.medicalHr", { defaultValue: "HR / medical" }),
    addTimeDebt: t("leaves:modal.leaveTypePicker.badges.addTimeDebt", { defaultValue: "+ time debt" }),
    reduceTimeDebt: t("leaves:modal.leaveTypePicker.badges.reduceTimeDebt", { defaultValue: "make-up credit" }),
    workTime: t("leaves:modal.leaveTypePicker.badges.workTime", { defaultValue: "Work-time" }),
    other: t("leaves:modal.leaveTypePicker.badges.other", { defaultValue: "Other" }),
    storno: t("leaves:modal.leaveTypePicker.badges.storno", { defaultValue: "Storno" }),
  }), [t]);

  const allItems = useMemo(() => {
    const rows = Array.isArray(leaveTypes) ? leaveTypes : [];

    const mapped = rows.map((lt) => {
      const classification = classifyLeaveType(lt);
      const label = getLeaveTypeLabel(lt);
      const code = normalizeCode(lt.code);
      const accountingType = normalizeAccountingType(lt.balance_accounting_type);
      const unit = normalizeUnit(lt.unit);

      return {
        id: String(lt.id),
        raw: lt,
        label,
        code,
        accountingType,
        unit,
        groupKey: classification.groupKey,
        groupLabel: groupLabels[classification.groupKey] || classification.groupKey,
        variant: classification.variant,
        badgeKey: classification.badgeKey,
        badgeLabel: badgeLabels[classification.badgeKey] || classification.badgeKey,
        sortWeight: classification.sortWeight,
        aliases: uniqStrings(codeAliases(code)),
      };
    });

    mapped.sort((a, b) => {
      const groupDiff = groupOrder.indexOf(a.groupKey) - groupOrder.indexOf(b.groupKey);
      if (groupDiff !== 0) return groupDiff;
      if (a.sortWeight !== b.sortWeight) return a.sortWeight - b.sortWeight;
      return a.label.localeCompare(b.label, "pl");
    });

    return mapped.map((item, index) => {
      const withQuickNo = {
        ...item,
        quickNo: quickNumber(index),
      };

      return {
        ...withQuickNo,
        searchText: buildSearchText(withQuickNo),
      };
    });
  }, [leaveTypes, groupLabels, badgeLabels, groupOrder]);

  const selectedItem = useMemo(
    () => allItems.find((item) => String(item.id) === String(value)) || null,
    [allItems, value]
  );

  const favoriteSet = useMemo(() => new Set((stored.favoriteIds || []).map(String)), [stored.favoriteIds]);

  const favoriteItems = useMemo(
    () => (stored.favoriteIds || [])
      .map((id) => allItems.find((item) => item.id === String(id)))
      .filter(Boolean),
    [stored.favoriteIds, allItems]
  );

  const recentItems = useMemo(
    () => (stored.recentIds || [])
      .map((id) => allItems.find((item) => item.id === String(id)))
      .filter(Boolean)
      .filter((item) => !favoriteSet.has(item.id)),
    [stored.recentIds, allItems, favoriteSet]
  );

  const filteredItems = useMemo(
    () => allItems.filter((item) => optionMatches(item, query)),
    [allItems, query]
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query, value]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  function persist(nextStored) {
    setStored(nextStored);
    writeStoredPickerState(storageScope, nextStored);
  }

  function rememberSelection(id) {
    const idStr = String(id);
    const nextRecentIds = [idStr, ...(stored.recentIds || []).filter((existing) => String(existing) !== idStr)].slice(0, MAX_RECENT_ITEMS);
    persist({
      ...stored,
      recentIds: nextRecentIds,
    });
  }

  function selectItem(item) {
    if (!item || disabled) return;
    onChange?.(item.id);
    rememberSelection(item.id);
    setQuery("");
    setOpen(false);
  }

  function toggleFavorite(item) {
    if (!item || disabled) return;

    const idStr = String(item.id);
    const current = new Set((stored.favoriteIds || []).map(String));
    if (current.has(idStr)) current.delete(idStr);
    else current.add(idStr);

    persist({
      ...stored,
      favoriteIds: Array.from(current),
    });
  }

  function selectByQuickNumber(rawValue) {
    const q = normalize(rawValue);
    if (!/^\d+$/.test(q)) return false;

    const padded = q.padStart(2, "0");
    const numeric = String(Number(q));
    const match = allItems.find((item) => item.quickNo === padded || String(Number(item.quickNo)) === numeric);
    if (!match) return false;

    selectItem(match);
    return true;
  }

  function onSearchKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, Math.max(0, filteredItems.length - 1)));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(0, prev - 1));
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(Math.max(0, filteredItems.length - 1));
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setQuery("");
      setOpen(false);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (selectByQuickNumber(query)) return;
      selectItem(filteredItems[activeIndex] || filteredItems[0]);
    }
  }

  function renderOption(item, index, source = "list") {
    const isSelected = String(item.id) === String(value);
    const isActive = source === "list" && index === activeIndex;
    const favorite = favoriteSet.has(item.id);

    return (
      <button
        key={`${source}-${item.id}`}
        type="button"
        className={[
          "lrcm-leaveTypePicker__option",
          `lrcm-leaveTypePicker__option--${item.variant}`,
          isSelected ? "lrcm-leaveTypePicker__option--selected" : "",
          isActive ? "lrcm-leaveTypePicker__option--active" : "",
        ].filter(Boolean).join(" ")}
        onClick={() => selectItem(item)}
        disabled={disabled}
        role="option"
        id={`leave-type-option-${item.id}`}
        aria-selected={isSelected}
      >
        <span className="lrcm-leaveTypePicker__quickNo" aria-label={t("leaves:modal.leaveTypePicker.quickNumberAria", { defaultValue: "Quick number {{number}}", number: item.quickNo })}>
          {item.quickNo}
        </span>

        <span className="lrcm-leaveTypePicker__optionMain">
          <span className="lrcm-leaveTypePicker__optionTitle">{item.label}</span>
          <span className="lrcm-leaveTypePicker__optionMeta">
            {item.code}
            {item.unit ? ` · ${item.unit}` : ""}
          </span>
        </span>

        <span className={`lrcm-leaveTypePicker__badge lrcm-leaveTypePicker__badge--${item.variant}`}>
          {item.badgeLabel}
        </span>

        <span
          className={[
            "lrcm-leaveTypePicker__favorite",
            favorite ? "lrcm-leaveTypePicker__favorite--active" : "",
          ].filter(Boolean).join(" ")}
          title={favorite
            ? t("leaves:modal.leaveTypePicker.removeFavorite", { defaultValue: "Remove from favorites" })
            : t("leaves:modal.leaveTypePicker.addFavorite", { defaultValue: "Add to favorites" })}
          aria-label={favorite
            ? t("leaves:modal.leaveTypePicker.removeFavorite", { defaultValue: "Remove from favorites" })
            : t("leaves:modal.leaveTypePicker.addFavorite", { defaultValue: "Add to favorites" })}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleFavorite(item);
          }}
        >
          {favorite ? "★" : "☆"}
        </span>
      </button>
    );
  }

  const groupedFilteredItems = useMemo(() => groupBy(filteredItems, (item) => item.groupKey), [filteredItems]);

  const collapsed = !open;

  return (
    <div
      ref={rootRef}
      className={[
        "lrcm-leaveTypePicker",
        selectedItem ? `lrcm-leaveTypePicker--${selectedItem.variant}` : "",
        disabled ? "lrcm-leaveTypePicker--disabled" : "",
      ].filter(Boolean).join(" ")}
    >
      <button
        type="button"
        className="lrcm-leaveTypePicker__summary"
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-expanded={open}
      >
        {selectedItem ? (
          <>
            <span className="lrcm-leaveTypePicker__summaryNo">{selectedItem.quickNo}</span>
            <span className="lrcm-leaveTypePicker__summaryText">
              <span className="lrcm-leaveTypePicker__summaryTitle">{selectedItem.label}</span>
              <span className="lrcm-leaveTypePicker__summaryMeta">
                {selectedItem.groupLabel} · {selectedItem.code}
              </span>
            </span>
            <span className={`lrcm-leaveTypePicker__badge lrcm-leaveTypePicker__badge--${selectedItem.variant}`}>
              {selectedItem.badgeLabel}
            </span>
            <span className="lrcm-leaveTypePicker__summaryAction">
              {t("leaves:modal.leaveTypePicker.change", { defaultValue: "Change" })}
            </span>
          </>
        ) : (
          <span className="lrcm-leaveTypePicker__summaryPlaceholder">
            {t("leaves:modal.leaveTypePicker.noSelection", { defaultValue: "Select leave type…" })}
          </span>
        )}
      </button>

      {open && (
        <div className="lrcm-leaveTypePicker__panel">
          <div className="lrcm-leaveTypePicker__searchRow">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
              disabled={disabled}
              placeholder={t("leaves:modal.leaveTypePicker.searchPlaceholder", {
                defaultValue: "Search by name, code, alias or number…",
              })}
              aria-label={t("leaves:modal.leaveTypePicker.searchAria", { defaultValue: "Search leave types" })}
              aria-activedescendant={filteredItems[activeIndex] ? `leave-type-option-${filteredItems[activeIndex].id}` : undefined}
            />
            <div className="lrcm-leaveTypePicker__searchHint">
              {t("leaves:modal.leaveTypePicker.keyboardHint", {
                defaultValue: "Type e.g. L4, storno, 31, wyjście; use ↑/↓ and Enter.",
              })}
            </div>
          </div>

          {!query && favoriteItems.length > 0 && (
            <section className="lrcm-leaveTypePicker__section">
              <div className="lrcm-leaveTypePicker__sectionTitle">
                {t("leaves:modal.leaveTypePicker.sections.favorites", { defaultValue: "Favorites" })}
              </div>
              <div className="lrcm-leaveTypePicker__options">
                {favoriteItems.map((item, index) => renderOption(item, index, "favorites"))}
              </div>
            </section>
          )}

          {!query && recentItems.length > 0 && (
            <section className="lrcm-leaveTypePicker__section">
              <div className="lrcm-leaveTypePicker__sectionTitle">
                {t("leaves:modal.leaveTypePicker.sections.recent", { defaultValue: "Recently used" })}
              </div>
              <div className="lrcm-leaveTypePicker__options">
                {recentItems.map((item, index) => renderOption(item, index, "recent"))}
              </div>
            </section>
          )}

          <section className="lrcm-leaveTypePicker__section">
            <div className="lrcm-leaveTypePicker__sectionTitle">
              {query
                ? t("leaves:modal.leaveTypePicker.sections.results", { defaultValue: "Search results" })
                : t("leaves:modal.leaveTypePicker.sections.all", { defaultValue: "All leave types" })}
            </div>

            {filteredItems.length === 0 ? (
              <div className="lrcm-leaveTypePicker__empty">
                {t("leaves:modal.leaveTypePicker.noResults", { defaultValue: "No matching leave type found." })}
              </div>
            ) : query ? (
              <div className="lrcm-leaveTypePicker__options" role="listbox">
                {filteredItems.map((item, index) => renderOption(item, index, "list"))}
              </div>
            ) : (
              groupOrder.map((groupKey) => {
                const rows = groupedFilteredItems.get(groupKey) || [];
                if (rows.length === 0) return null;

                return (
                  <div key={groupKey} className="lrcm-leaveTypePicker__groupBlock">
                    <div className="lrcm-leaveTypePicker__groupTitle">
                      {groupLabels[groupKey] || groupKey}
                    </div>
                    <div className="lrcm-leaveTypePicker__options" role="listbox">
                      {rows.map((item) => renderOption(item, filteredItems.indexOf(item), "list"))}
                    </div>
                  </div>
                );
              })
            )}
          </section>

          {collapsed ? null : (
            <div className="lrcm-leaveTypePicker__footerHelp">
              {t("leaves:modal.leaveTypePicker.footerHelp", {
                defaultValue: "Numbers are quick UI shortcuts for this picker. Formal processing still uses the backend leave type code.",
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
